<script setup lang="ts">
import { useSerialize } from "@lxc/app-device-common";
import type { Rules } from "async-validator";
import { HttpStatusCode } from "axios";
import type { Ref, WritableComputedRef } from "vue";
import { ref } from "vue";
import { VALIDATION_REGEXP } from "~/core/constants/constants";
import LxcError from "~/core/models/LxcError.class";
import {
  NotificationKey,
  showNotificationError,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useExportCertificateAsString } from "~/modules/certificate/composables/useCertificateExport";
import { CACertificateItemType } from "~/modules/certificate/models/CACertificateItemType.enum";
import type { KeystoreCSRRequest } from "~/modules/certificate/models/KeystoreCSRRequest.interface";
import keystoreService from "~/modules/certificate/services/keystore.service";
import type { KeystoreForm } from "~/types";
import { getValuesFromUniqueLabels } from "~/utils/unique-label-tools";

const { t } = useI18n();
const serialize = useSerialize();
const { exportCertificateAsString } = useExportCertificateAsString();

const props = defineProps<{
  disabled?: false;
  isTagsLoading: boolean;
  sideCanvasShown: boolean;
  tags?: string[] | null;
  tagsError?: LxcError | null;
}>();

const isLoading: Ref<boolean> = ref(false);

const emit = defineEmits(["save", "update:sideCanvasShown"]);

const defaultCSRRequestForm: KeystoreForm = {
  alias: "",
  subject: {
    cn: "",
    email: "",
    serialNumber: undefined,
    organizationUnit: undefined,
    organization: undefined,
    locality: undefined,
    region: undefined,
    country: undefined,
  },
  tags: [],
  certificate: "",
  chain: undefined,
  subjectAlternativeNames: [],
  extendedKeyUsageIds: [],
  automaticallyRenewed: false,
};

const formSideCanvasShown: WritableComputedRef<boolean> = computed({
  get() {
    return props.sideCanvasShown;
  },
  set(sideCanvasShown: boolean) {
    emit("update:sideCanvasShown", sideCanvasShown);
  },
});
const csrRequestForm: Ref<KeystoreForm> = ref<KeystoreForm>({
  ...defaultCSRRequestForm,
  subject: { ...defaultCSRRequestForm.subject },
  tags: [...defaultCSRRequestForm.tags],
  subjectAlternativeNames: [...defaultCSRRequestForm.subjectAlternativeNames],
  extendedKeyUsageIds: [...defaultCSRRequestForm.extendedKeyUsageIds],
});
const defaultCSRRequestFormStringified = serialize(defaultCSRRequestForm);
const csrRequestFormRef = ref();
const genericFormRef = ref();
const edited = computed(() => {
  const stringifiedForm = serialize(csrRequestForm.value);
  return stringifiedForm !== defaultCSRRequestFormStringified;
});

// Certificate rules
const rules = computed(() => {
  const rulesConfig: Rules = {
    alias: [
      {
        required: true,
        message: t("certificates.validation.alias.required"),
        type: "string",
        whitespace: false,
      },
      {
        type: "string",
        message: t("certificates.validation.alias.invalid"),
        pattern: VALIDATION_REGEXP.CERTIFICATE_ALIAS,
      },
    ],
    "subject.cn": [
      {
        required: true,
        message: t("certificates.validation.cn"),
        type: "string",
        whitespace: false,
      },
    ],
    "subject.email": [
      {
        required: true,
        message: t("certificates.validation.email"),
        whitespace: false,
      },
      {
        type: "email",
        message: t("input.error.invalidFormat"),
      },
    ],
  };

  return rulesConfig;
});

function setCSRRequestForm() {
  Object.assign(csrRequestForm.value, defaultCSRRequestForm, {
    subject: { ...defaultCSRRequestForm.subject },
    tags: [...defaultCSRRequestForm.tags],
    subjectAlternativeNames: [...defaultCSRRequestForm.subjectAlternativeNames],
    extendedKeyUsageIds: [...defaultCSRRequestForm.extendedKeyUsageIds],
  });
  setTimeout(csrRequestFormRef.value?.clearValidate, 0);
}

async function saveForm(): Promise<void> {
  isLoading.value = true;
  csrRequestForm.value.alias = csrRequestForm.value.alias.trim();

  const csrRequest: KeystoreCSRRequest = {
    ...csrRequestForm.value,
    tags: getValuesFromUniqueLabels(csrRequestForm.value.tags),
  };

  const response =
    await keystoreService.generateCertificateSigningRequest(csrRequest);

  if (LxcError.check(response)) {
    if (response.status === HttpStatusCode.Conflict) {
      showNotificationError(
        t("notification.error.keystore.aliasAlreadyUsed.title"),
        t("notification.error.keystore.aliasAlreadyUsed.message"),
        false,
      );
    } else {
      response.notify(NotificationKey.saveError);
    }
  } else {
    if (response.csr) {
      exportCertificateAsString(
        response.csr,
        response.alias ?? "csr",
        CACertificateItemType.CSR,
      );
      showNotificationSuccess(t(NotificationKey.saveSuccess));
      close();
      setCSRRequestForm();
      emit("save");
    } else {
      showNotificationError(
        "notification.error.certificate.parsing",
        "notification.error.certificate.notValid",
      );
    }
  }

  isLoading.value = false;
}

async function validate(): Promise<boolean> {
  const [csrResult, genericResult] = await Promise.all([
    csrRequestFormRef.value?.validate().catch(() => false),
    genericFormRef.value?.validate().catch(() => false),
  ]);

  return csrResult && genericResult;
}

const onSubmit = async (): Promise<boolean> => {
  const dataValid = await validate();

  if (dataValid) {
    await saveForm();
  }

  return dataValid;
};

function close() {
  formSideCanvasShown.value = false;
}

const onCancel = () => {
  setCSRRequestForm();
  close();
};
</script>

<template>
  <lxc-side-canvas
    v-model:show="formSideCanvasShown"
    size="1/3"
    :header="t('certificates.keystore.generateCSR')"
    :close-tooltip="t('button.close')"
    :confirm-enabled="edited"
    :confirm-title="t('certificates.keystore.generateCSR')"
    :confirm-message="t('certificates.cancelAction.confirm.message')"
    :confirm-ok-label="t('button.confirm')"
    :confirm-cancel-label="t('button.cancel')"
    @discard="onCancel"
  >
    <lxc-form
      ref="csrRequestFormRef"
      :model="csrRequestForm"
      :rules="rules"
      @submit.prevent="onSubmit"
    >
      <keystore-generic-generation-form
        ref="genericFormRef"
        v-model="csrRequestForm"
        :disabled="false"
        :edition="false"
        :is-tags-loading="isTagsLoading"
        :tags="tags"
        :tags-error="tagsError"
      />
    </lxc-form>

    <template #footer>
      <div class="grid grid-cols-[max-content_auto] gap-4">
        <lxc-button
          html-type="button"
          type="secondary"
          :title="t('button.close')"
          @click="onCancel"
        >
          {{ t("button.close") }}
        </lxc-button>
        <lxc-button
          html-type="submit"
          :disabled="!edited || isLoading"
          :title="t('certificates.keystore.generateCSR')"
          @click="onSubmit"
        >
          {{ t("certificates.keystore.generateCSR") }}
        </lxc-button>
      </div>
    </template>
  </lxc-side-canvas>
</template>
